import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Briefcase,
  CalendarOff,
  CheckSquare,
  MessageSquare,
  Star,
  UserCircle,
  type LucideIcon,
} from 'lucide-react'
import { apiFetch } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'

// ─── Types ───────────────────────────────────────────────────────────────────

interface DashboardGig {
  id: string
  role: string
  status: string
  startDate: string
  endDate: string
  artistId: string
  show: {
    title: string
    venue: string
    city: string
    date: string
  } | null
}

interface DashboardData {
  profile: {
    displayName: string
    primaryRole: string
    completeness: number
    missingFields: string[]
  }
  upcomingGigs: DashboardGig[]
  unavailableDays: number
  testimonialCount: number
  pendingRequestCount: number
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CrewDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['crew', 'dashboard'],
    queryFn: () => apiFetch<DashboardData>('/crew/profile/dashboard'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-ovation-muted">
        Loading dashboard...
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-24 text-center text-red-600">
        Failed to load dashboard. Please try again.
      </div>
    )
  }

  // No profile yet
  if (!data) {
    return (
      <div className="mx-auto max-w-md py-24 text-center">
        <UserCircle className="mx-auto mb-4 h-12 w-12 text-ovation-muted" />
        <h2 className="font-display text-xl">No crew profile yet</h2>
        <p className="mt-2 text-sm text-ovation-muted">
          Create your crew profile to start receiving gig offers and build your reputation.
        </p>
        <Link
          to="/crew/profile"
          className="mt-4 inline-block rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper hover:opacity-90"
        >
          Create profile
        </Link>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title={`Welcome back, ${data.profile.displayName}`}
        subtitle={data.profile.primaryRole.replace(/_/g, ' ')}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Upcoming Gigs */}
        <DashboardCard
          icon={Briefcase}
          title="Upcoming Gigs"
          linkTo="/crew/engagements"
          linkLabel="View all"
        >
          {data.upcomingGigs.length === 0 ? (
            <p className="text-sm text-ovation-muted">No upcoming gigs</p>
          ) : (
            <ul className="space-y-2">
              {data.upcomingGigs.map((gig) => (
                <li key={gig.id} className="text-sm">
                  <div className="font-medium">
                    {gig.show?.title ?? 'TBC'}{' '}
                    <span className="text-xs text-ovation-muted">
                      ({gig.status})
                    </span>
                  </div>
                  <div className="text-xs text-ovation-muted">
                    {gig.show?.venue && `${gig.show.venue}, `}
                    {gig.show?.city ?? ''}
                    {gig.show?.date &&
                      ` \u2014 ${new Date(gig.show.date).toLocaleDateString()}`}
                  </div>
                  <div className="text-xs text-ovation-muted">
                    Role: {gig.role.replace(/_/g, ' ')}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DashboardCard>

        {/* Unavailability */}
        <DashboardCard
          icon={CalendarOff}
          title="Unavailability"
          linkTo="/crew/availability"
          linkLabel="View all"
        >
          <p className="text-3xl font-bold">{data.unavailableDays}</p>
          <p className="text-xs text-ovation-muted">blocked days in next 30 days</p>
        </DashboardCard>

        {/* Testimonials */}
        <DashboardCard
          icon={Star}
          title="Testimonials"
          linkTo="/crew/profile"
          linkLabel="View all"
        >
          <p className="text-3xl font-bold">{data.testimonialCount}</p>
          <p className="text-xs text-ovation-muted">received</p>
          {data.pendingRequestCount > 0 && (
            <p className="mt-1 text-xs text-amber-600">
              {data.pendingRequestCount} pending request
              {data.pendingRequestCount > 1 ? 's' : ''}
            </p>
          )}
        </DashboardCard>

        {/* Profile Completeness */}
        <DashboardCard
          icon={UserCircle}
          title="Profile Completeness"
          linkTo="/crew/profile"
          linkLabel="Edit profile"
        >
          <div className="mb-1 flex items-end gap-2">
            <span className="text-3xl font-bold">{data.profile.completeness}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-ovation-ink/10">
            <div
              className="h-full rounded-full bg-ovation-ink transition-all"
              style={{ width: `${data.profile.completeness}%` }}
            />
          </div>
          {data.profile.missingFields.length > 0 && (
            <p className="mt-2 text-xs text-ovation-muted">
              Missing: {data.profile.missingFields.join(', ')}
            </p>
          )}
        </DashboardCard>

        {/* My Checklist — placeholder */}
        <DashboardCard icon={CheckSquare} title="My Checklist">
          <p className="text-sm text-ovation-muted">Coming soon</p>
        </DashboardCard>

        {/* Messages — placeholder */}
        <DashboardCard icon={MessageSquare} title="Messages">
          <p className="text-sm text-ovation-muted">Coming soon</p>
        </DashboardCard>
      </div>
    </>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface DashboardCardProps {
  icon: LucideIcon
  title: string
  linkTo?: string
  linkLabel?: string
  children: React.ReactNode
}

function DashboardCard({ icon: Icon, title, linkTo, linkLabel, children }: DashboardCardProps) {
  return (
    <div className="flex flex-col rounded-lg border border-ovation-ink/10 bg-ovation-paper p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-ovation-muted" />
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {linkTo && linkLabel && (
          <Link
            to={linkTo}
            className="text-xs text-ovation-muted hover:text-ovation-ink"
          >
            {linkLabel}
          </Link>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  )
}
